import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_FORM = {
    zona_id: '',
    tecnologia_id: '',
    nombre: '',
    velocidad_subida: '',
    velocidad_descarga: '',
    cargo_fijo_mensual: '',
    cargo_instalacion: '',
    cargo_reconexion: '',
    activo: true
};

const formatMoney = (val) => Number(val || 0).toLocaleString('es-CO');

const Planes = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [zonas, setZonas] = useState([]);
    const [tecnologias, setTecnologias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const [clientesModalOpen, setClientesModalOpen] = useState(false);
    const [clientesPlan, setClientesPlan] = useState(null);
    const [clientesLista, setClientesLista] = useState([]);
    const [clientesLoading, setClientesLoading] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [planesRes, zonasRes, tecRes] = await Promise.all([
                api.get('/planes?all=1'),
                api.get('/zonas?all=1'),
                api.get('/tecnologias')
            ]);
            setItems(planesRes.data);
            setZonas(zonasRes.data);
            setTecnologias(tecRes.data);
        } catch {
            setError('Error cargando planes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            zona_id: item.zona_id,
            tecnologia_id: item.tecnologia_id,
            nombre: item.nombre,
            velocidad_subida: item.velocidad_subida,
            velocidad_descarga: item.velocidad_descarga,
            cargo_fijo_mensual: item.cargo_fijo_mensual,
            cargo_instalacion: item.cargo_instalacion,
            cargo_reconexion: item.cargo_reconexion,
            activo: !!Number(item.activo)
        });
        setEditingId(item.id);
        setError('');
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const openClientesModal = async (plan) => {
        setClientesPlan(plan);
        setClientesModalOpen(true);
        setClientesLoading(true);
        setClientesLista([]);
        try {
            const { data } = await api.get(`/planes/${plan.id}/clientes`);
            setClientesLista(data);
        } catch {
            setClientesLista([]);
        } finally {
            setClientesLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editingId) {
                await api.put(`/planes/${editingId}`, form);
                showSuccess('Plan actualizado correctamente');
            } else {
                await api.post('/planes', form);
                showSuccess('Plan creado correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando plan');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este plan? Solo es posible si no tiene contratos activos.')) return;
        try {
            await api.delete(`/planes/${id}`);
            showSuccess('Plan eliminado correctamente');
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'No se puede eliminar este plan');
        }
    };

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Planes</h2>
                {can('planes', 'create') && (
                    <button
                        onClick={openCreate}
                        className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                        + Agregar Nuevo
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No hay planes registrados</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Plan</th>
                                <th className="px-4 py-3 text-left">Zona</th>
                                <th className="px-4 py-3 text-left">Tecnología</th>
                                <th className="px-4 py-3 text-left">Velocidad</th>
                                <th className="px-4 py-3 text-left">Mensual</th>
                                <th className="px-4 py-3 text-left">Instalación</th>
                                <th className="px-4 py-3 text-left">Reconexión</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                <th className="px-4 py-3 text-left">Clientes</th>
                                {can('planes', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.zona_nombre} ({item.zona_codigo})</td>
                                    <td className="px-4 py-3 text-gray-500">{item.tecnologia_nombre}</td>
                                    <td className="px-4 py-3 text-gray-600">{item.velocidad_descarga}/{item.velocidad_subida} MB</td>
                                    <td className="px-4 py-3 text-gray-600">${formatMoney(item.cargo_fijo_mensual)}</td>
                                    <td className="px-4 py-3 text-gray-600">${formatMoney(item.cargo_instalacion)}</td>
                                    <td className="px-4 py-3 text-gray-600">${formatMoney(item.cargo_reconexion)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${Number(item.activo) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {Number(item.activo) ? 'Activo' : 'Cerrado'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => openClientesModal(item)}
                                            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                                        >
                                            Ver ({item.clientes_activos})
                                        </button>
                                    </td>
                                    {can('planes', 'update') && (
                                        <td className="px-4 py-3 text-right flex gap-2 justify-end">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                            <button onClick={() => handleDelete(item.id)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50">
                                                Eliminar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar plan' : 'Nuevo plan'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div>
                                <label className={labelClass}>Nombre del plan *</label>
                                <input name="nombre" value={form.nombre} onChange={handleChange} className={inputClass} placeholder="Ej: PLAN F.O SUPER 50 MEGAS" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Zona *</label>
                                    <select name="zona_id" value={form.zona_id} onChange={handleChange} className={inputClass}>
                                        <option value="">Seleccionar...</option>
                                        {zonas.map(z => (
                                            <option key={z.id} value={z.id}>{z.nombre} ({z.codigo})</option>
                                        ))}
                                    </select>
                                    {form.zona_id && (() => {
                                        const z = zonas.find(z => String(z.id) === String(form.zona_id));
                                        return z?.cod_divipola ? (
                                            <div className="mt-1 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                                                <span className="text-xs text-blue-600">DIVIPOLA:</span>
                                                <span className="text-xs font-mono font-bold text-blue-800">{z.cod_divipola}</span>
                                                <button type="button" onClick={() => navigator.clipboard.writeText(z.cod_divipola)}
                                                    className="ml-auto text-xs text-blue-500 hover:text-blue-700">
                                                    📋 Copiar
                                                </button>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                                <div>
                                    <label className={labelClass}>Tecnología *</label>
                                    <select name="tecnologia_id" value={form.tecnologia_id} onChange={handleChange} className={inputClass}>
                                        <option value="">Seleccionar...</option>
                                        {tecnologias.map(t => (
                                            <option key={t.id} value={t.id}>{t.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Velocidad descarga (MB) *</label>
                                    <input name="velocidad_descarga" type="number" min="1" value={form.velocidad_descarga} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Velocidad subida (MB) *</label>
                                    <input name="velocidad_subida" type="number" min="1" value={form.velocidad_subida} onChange={handleChange} className={inputClass} />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelClass}>Cargo mensual *</label>
                                    <input name="cargo_fijo_mensual" type="number" min="0" value={form.cargo_fijo_mensual} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Cargo instalación</label>
                                    <input name="cargo_instalacion" type="number" min="0" value={form.cargo_instalacion} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Cargo reconexión</label>
                                    <input name="cargo_reconexion" type="number" min="0" value={form.cargo_reconexion} onChange={handleChange} className={inputClass} />
                                </div>
                            </div>

                            {editingId && (
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} />
                                    Plan activo (disponible para nuevos clientes)
                                </label>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear plan')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: clientes usando este plan */}
            {clientesModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">
                                Clientes en "{clientesPlan?.nombre}"
                            </h3>
                            <button onClick={() => setClientesModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-4">
                            {clientesLoading ? (
                                <div className="p-6 text-center text-gray-400 text-sm">Cargando...</div>
                            ) : clientesLista.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    Ningún cliente activo está usando este plan.
                                    {Number(clientesPlan?.activo) === 1 && (
                                        <p className="mt-1">Puedes cerrarlo desde "Editar".</p>
                                    )}
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="text-gray-500 text-xs uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Cliente</th>
                                            <th className="px-3 py-2 text-left">Documento</th>
                                            <th className="px-3 py-2 text-left">Teléfono</th>
                                            <th className="px-3 py-2 text-left">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {clientesLista.map(c => (
                                            <tr key={c.uuid}>
                                                <td className="px-3 py-2 font-medium text-gray-800">{c.nombre_completo}</td>
                                                <td className="px-3 py-2 text-gray-600">
                                                    {c.tipo_documento === 'nit' ? 'NIT' : 'CC'} {c.documento}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">{c.telefono || '—'}</td>
                                                <td className="px-3 py-2 text-gray-500">{c.estado}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="px-6 pb-6">
                            <button
                                onClick={() => setClientesModalOpen(false)}
                                className="w-full border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Planes;
